import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

export type OrderableProduct = {
  code: string;
  name: string;
};

type OrderFormState = {
  senderName: string;
  senderPhone: string;
  receiverName: string;
  receiverPhone: string;
  deliveryOrPickup: 'delivery' | 'pickup';
  address: string;
  card: 'yes' | 'no';
  cardMessage: string;
  preferredDate: string;
  timeSlot: '1' | '2' | '3';
  acceptsVariationNotice: boolean;
};

type OrderModalContextValue = {
  openOrderModal: (product: OrderableProduct) => void;
  closeOrderModal: () => void;
  isOrderModalOpen: boolean;
};

const WHATSAPP_NUMBER = '5977130133';
const WHATSAPP_BASE_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=`;

const OrderModalContext = createContext<OrderModalContextValue | null>(null);

const openWhatsApp = (message: string) => {
  window.open(`${WHATSAPP_BASE_URL}${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
};

const getOrderMessage = (product: OrderableProduct, form: OrderFormState) =>
  [
    'Hi, I would like to order:',
    '',
    `Product: ${product.code} - ${product.name}`,
    '',
    `Sender: ${form.senderName}, phone number: ${form.senderPhone}`,
    '',
    `Receiver: ${form.receiverName}, phone number: ${form.receiverPhone}`,
    '',
    `Delivery or pickup: ${form.deliveryOrPickup}, preferred date: ${form.preferredDate}`,
    '',
    `Address: ${form.deliveryOrPickup === 'delivery' ? form.address : 'not applicable'}`,
    '(Note: the delivery cost will be confirmed before order approval)',
    '',
    `Card: ${form.card}, message: ${form.card === 'yes' ? form.cardMessage : 'not applicable'}`,
    '',
    `Time slot: ${form.timeSlot}, ${form.timeSlot === '1' ? '09:00 - 12:00' : form.timeSlot === '2' ? '12:00 - 15:00' : '15:00 - 18:00'}`,
  ].join('\n');

const OrderModal: React.FC<{
  product: OrderableProduct;
  onClose: () => void;
}> = ({ product, onClose }) => {
  const [form, setForm] = useState<OrderFormState>({
    senderName: '',
    senderPhone: '',
    receiverName: '',
    receiverPhone: '',
    deliveryOrPickup: 'delivery',
    address: '',
    card: 'no',
    cardMessage: '',
    preferredDate: '',
    timeSlot: '1',
    acceptsVariationNotice: false,
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const updateField = <K extends keyof OrderFormState>(field: K, value: OrderFormState[K]) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    openWhatsApp(getOrderMessage(product, form));
    onClose();
  };

  return createPortal(
    <div className="md-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="md-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="md-modal-title"
      >
        <p className="md-modal-label">Order details</p>
        <h3 id="md-modal-title">Complete your order request</h3>
        <p className="md-modal-product">
          {product.code} - {product.name}
        </p>
        <form className="md-order-form" onSubmit={handleSubmit}>
          <div className="md-form-grid">
            <label className="md-field">
              <span>Sender name</span>
              <input
                type="text"
                value={form.senderName}
                onChange={(event) => updateField('senderName', event.target.value)}
                required
              />
            </label>
            <label className="md-field">
              <span>Sender phone number</span>
              <input
                type="tel"
                value={form.senderPhone}
                onChange={(event) => updateField('senderPhone', event.target.value)}
                required
              />
            </label>
            <label className="md-field">
              <span>Receiver name</span>
              <input
                type="text"
                value={form.receiverName}
                onChange={(event) => updateField('receiverName', event.target.value)}
                required
              />
            </label>
            <label className="md-field">
              <span>Receiver phone number</span>
              <input
                type="tel"
                value={form.receiverPhone}
                onChange={(event) => updateField('receiverPhone', event.target.value)}
                required
              />
            </label>
            <label className="md-field">
              <span>Delivery or pickup</span>
              <select
                value={form.deliveryOrPickup}
                onChange={(event) =>
                  updateField('deliveryOrPickup', event.target.value as OrderFormState['deliveryOrPickup'])
                }
              >
                <option value="delivery">Delivery</option>
                <option value="pickup">Pickup</option>
              </select>
            </label>
            <label className="md-field">
              <span>Preferred date</span>
              <input
                type="date"
                value={form.preferredDate}
                onChange={(event) => updateField('preferredDate', event.target.value)}
                required
              />
            </label>
            <label className="md-field md-field-full">
              <span>Full address</span>
              <textarea
                value={form.address}
                onChange={(event) => updateField('address', event.target.value)}
                required={form.deliveryOrPickup === 'delivery'}
                placeholder="Required for delivery orders"
              />
            </label>
            <label className="md-field">
              <span>Card</span>
              <select
                value={form.card}
                onChange={(event) => updateField('card', event.target.value as OrderFormState['card'])}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </label>
            <label className="md-field">
              <span>Time slot</span>
              <select
                value={form.timeSlot}
                onChange={(event) =>
                  updateField('timeSlot', event.target.value as OrderFormState['timeSlot'])
                }
              >
                <option value="1">1 = 09:00 - 12:00</option>
                <option value="2">2 = 12:00 - 15:00</option>
                <option value="3">3 = 15:00 - 18:00</option>
              </select>
            </label>
            <label className="md-field md-field-full">
              <span>Card message</span>
              <textarea
                value={form.cardMessage}
                onChange={(event) => updateField('cardMessage', event.target.value)}
                required={form.card === 'yes'}
                placeholder="Only needed if you want a card"
              />
            </label>
          </div>
          <p className="md-form-note">
            The delivery cost will be confirmed before order approval.
          </p>
          <label className="md-consent">
            <input
              type="checkbox"
              checked={form.acceptsVariationNotice}
              onChange={(event) => updateField('acceptsVariationNotice', event.target.checked)}
              required
            />
            <span>
              I understand that each arrangement is uniquely created and that flowers,
              colors, and wrapping may vary slightly due to availability. Images are for
              illustration purposes only.
            </span>
          </label>
          <div className="md-modal-actions">
            <button type="button" className="md-button md-button-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="md-button md-button-primary"
              disabled={!form.acceptsVariationNotice}
            >
              Continue to WhatsApp
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export const OrderModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedProduct, setSelectedProduct] = useState<OrderableProduct | null>(null);

  useEffect(() => {
    if (!selectedProduct) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedProduct]);

  const value = useMemo<OrderModalContextValue>(
    () => ({
      openOrderModal: setSelectedProduct,
      closeOrderModal: () => setSelectedProduct(null),
      isOrderModalOpen: selectedProduct !== null,
    }),
    [selectedProduct]
  );

  return (
    <OrderModalContext.Provider value={value}>
      {children}
      {selectedProduct ? (
        <OrderModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      ) : null}
    </OrderModalContext.Provider>
  );
};

export const useOrderModal = () => {
  const context = useContext(OrderModalContext);

  if (!context) {
    throw new Error('useOrderModal must be used within an OrderModalProvider');
  }

  return context;
};
